/**
 * Backup automático separado por aba/módulo — cada pasta guarda só os dados daquela área.
 * Rotativo: mantém as últimas N cópias com data/hora no nome.
 */

import type { BackupCompletoPayload } from './adeBackupCompleto';
import { restaurarEntradasBackupParcial } from './adeBackupCompleto';

export const PASTA_ROTATIVO = 'rotativo';
export const ARQUIVO_ULTIMO = 'ultimo.json';
/** Cópias horárias por módulo (24 = últimas 24 horas se backup roda a cada 1h). */
export const MAX_ROTATIVOS_POR_MODULO = 24;
/** Cópias do backup completo. */
export const MAX_ROTATIVOS_COMPLETO = 48;

export interface DefinicaoModuloBackup {
  id: string;
  label: string;
  /** Chaves exatas do localStorage. */
  chavesLocalStorage: readonly string[];
  /** Qualquer chave LS que comece com um destes prefixos. */
  prefixosLocalStorage?: readonly string[];
  /** Testado contra chaves do IndexedDB exportado (`db/store/key`). */
  padroesIndexedDB: readonly RegExp[];
}

export interface BackupModuloPayload {
  _tipo: 'backup-ade-modulo';
  _modulo: string;
  _label: string;
  _versao: string;
  _data: string;
  dados: Record<string, string>;
  indexedDB: Record<string, unknown>;
  resumo: Record<string, number>;
}

export const MODULOS_BACKUP: readonly DefinicaoModuloBackup[] = [
  {
    id: 'catalogo',
    label: 'Catálogo',
    chavesLocalStorage: [
      'planos-corte-ade',
      'checklist-impressao-projeto-ade',
      'catalogo-projetos-excluidos-ade',
      'migracao-berlim-berlin-v1',
    ],
    padroesIndexedDB: [/zustandPersist\/planos-corte-ade$/],
  },
  {
    id: 'lotes',
    label: 'Lotes',
    chavesLocalStorage: [
      'lotes-produtos-ade',
      'lotes-produtos-excluidos-ade',
      'lotes-finalizados-manual-ade',
      'lotes-impressos-palete-ade',
    ],
    padroesIndexedDB: [/lotesProdutos\/lotesProdutos$/],
  },
  {
    id: 'tempo-corte',
    label: 'Tempo de Corte Exato por Pontuação',
    chavesLocalStorage: [
      'produtos-tempo-corte-ade',
      'produtos-tempo-corte-excluidos-ade',
      'tempo-corte-itens-ade',
      'relatorio-tc-ignorados-ade',
    ],
    prefixosLocalStorage: ['tempo-corte-'],
    padroesIndexedDB: [
      /tempoCorte\/produtosTempoCorteSalvos$/,
      /tempoCorte\/tempoCorteItens$/,
      /tempoCorte\/lotesProdutos$/,
    ],
  },
  {
    id: 'historico',
    label: 'Histórico de impressões',
    chavesLocalStorage: ['historico-impressoes-ade'],
    padroesIndexedDB: [/historicoImpressoes\/historicoImpressoes$/],
  },
  {
    id: 'relatorio',
    label: 'Relatório',
    chavesLocalStorage: [],
    padroesIndexedDB: [/relatorioPecas\/relatorioPecas$/],
  },
  {
    id: 'sobra',
    label: 'Sobra',
    chavesLocalStorage: [
      'sobra-pecas-ade',
      'sobra-pastas-ade',
      'sobra-riscados-ade',
      'sobra-pecas-excluidos-ade',
    ],
    padroesIndexedDB: [
      /sobraPecas\/sobraPecas$/,
      /sobraPastas\/sobraPastas$/,
      /sobraRiscados\/sobraRiscados$/,
    ],
  },
  {
    id: 'boletim',
    label: 'Boletim / Tabela dos Dias',
    chavesLocalStorage: [
      'tabela-dias-pontuacoes-ade',
      'pontuacoes-produtos-ade',
      'grafico-mes-inicio-boletim-ade',
      'ade-calc-planilha-v1',
    ],
    prefixosLocalStorage: ['boletim-'],
    padroesIndexedDB: [/^ade-calc-planilha-db\//],
  },
  {
    id: 'copa',
    label: 'Resultados da Copa',
    chavesLocalStorage: [],
    prefixosLocalStorage: ['copa-'],
    padroesIndexedDB: [/^ade-copa-db\//],
  },
  {
    id: 'layout',
    label: 'Layout de impressão',
    chavesLocalStorage: ['layout-impressao-ade'],
    padroesIndexedDB: [/layoutImpressao\/layoutImpressao$/],
  },
  {
    id: 'auditoria',
    label: 'Auditoria',
    chavesLocalStorage: [],
    padroesIndexedDB: [/auditoriaAcoes\/auditoriaAcoes$/],
  },
  {
    id: 'palete',
    label: 'Produto por Palete',
    chavesLocalStorage: [],
    padroesIndexedDB: [/^ade-produto-palete-db\//],
  },
  {
    id: 'tempo-producao-peca',
    label: 'Tempo de Corte por Peça em Produção',
    chavesLocalStorage: [],
    padroesIndexedDB: [/^ade-tempo-producao-peca-db\//],
  },
  {
    id: 'adiantamento-mdf',
    label: 'Adiantamento de MDF',
    chavesLocalStorage: ['ade-adiantamento-mdf-v1'],
    prefixosLocalStorage: ['ade-adiantamento-mdf-'],
    padroesIndexedDB: [/^ade-adiantamento-mdf-db\//],
  },
  {
    id: 'tempo-embalar',
    label: 'Tempo para Embalar',
    chavesLocalStorage: [],
    prefixosLocalStorage: ['tempo-embalar-'],
    padroesIndexedDB: [/^ade-tempo-embalar-db\//],
  },
] as const;

function chavePertenceModuloLs(chave: string, mod: DefinicaoModuloBackup): boolean {
  if (mod.chavesLocalStorage.includes(chave)) return true;
  for (const p of mod.prefixosLocalStorage ?? []) {
    if (chave.startsWith(p)) return true;
  }
  return false;
}

function chavePertenceModuloIdb(pathKey: string, mod: DefinicaoModuloBackup): boolean {
  return mod.padroesIndexedDB.some((rx) => rx.test(pathKey));
}

function contarItensModulo(payload: BackupModuloPayload): Record<string, number> {
  const resumo: Record<string, number> = {
    chavesLocalStorage: Object.keys(payload.dados).length,
    chavesIndexedDB: Object.keys(payload.indexedDB).length,
  };
  try {
    const planos = payload.dados['planos-corte-ade'];
    if (planos) {
      const p = JSON.parse(planos) as { state?: { projetosSalvos?: unknown[] } };
      resumo.projetosCatalogo = p?.state?.projetosSalvos?.length ?? 0;
    }
    const lotes = payload.indexedDB['ade-tempo-corte-db/lotesProdutos/lotesProdutos'];
    if (Array.isArray(lotes)) resumo.lotes = lotes.length;
    const hist = payload.indexedDB['ade-tempo-corte-db/historicoImpressoes/historicoImpressoes'];
    if (Array.isArray(hist)) resumo.historicoImpressoes = hist.length;
    const tc = payload.indexedDB['ade-tempo-corte-db/tempoCorte/produtosTempoCorteSalvos'];
    if (Array.isArray(tc)) resumo.produtosTempoCorte = tc.length;
    const copa = payload.indexedDB['ade-copa-db/copaEstado/estadoAtual'];
    if (copa && typeof copa === 'object') {
      const parts = (copa as { participantes?: unknown[] }).participantes;
      if (Array.isArray(parts)) resumo.participantesCopa = parts.length;
    }
    const copaLs = payload.dados['copa-pool-ade-v2'];
    if (copaLs) {
      const p = JSON.parse(copaLs) as { participantes?: unknown[] };
      if (Array.isArray(p.participantes)) resumo.participantesCopa = p.participantes.length;
    }
  } catch {
    /* resumo parcial */
  }
  return resumo;
}

export function extrairBackupModulo(
  completo: BackupCompletoPayload,
  mod: DefinicaoModuloBackup
): BackupModuloPayload {
  const dados: Record<string, string> = {};
  for (const [k, v] of Object.entries(completo.dados)) {
    if (chavePertenceModuloLs(k, mod)) dados[k] = v;
  }
  const indexedDB: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(completo.indexedDB)) {
    if (chavePertenceModuloIdb(k, mod)) indexedDB[k] = v;
  }
  const payload: BackupModuloPayload = {
    _tipo: 'backup-ade-modulo',
    _modulo: mod.id,
    _label: mod.label,
    _versao: completo._versao,
    _data: completo._data,
    dados,
    indexedDB,
    resumo: {},
  };
  payload.resumo = contarItensModulo(payload);
  return payload;
}

export function extrairTodosBackupsModulo(
  completo: BackupCompletoPayload
): BackupModuloPayload[] {
  return MODULOS_BACKUP.map((mod) => extrairBackupModulo(completo, mod)).filter(
    (p) => Object.keys(p.dados).length > 0 || Object.keys(p.indexedDB).length > 0
  );
}

export function moduloBackupPorId(id: string): DefinicaoModuloBackup | undefined {
  return MODULOS_BACKUP.find((m) => m.id === id);
}

export async function restaurarBackupModulo(
  texto: string
): Promise<{ ok: boolean; modulo?: string; label?: string; chaves: number; chavesIdb: number; erro?: string }> {
  try {
    const parsed = JSON.parse(texto) as BackupModuloPayload;
    if (parsed?._tipo !== 'backup-ade-modulo' || !parsed._modulo) {
      return { ok: false, chaves: 0, chavesIdb: 0, erro: 'Arquivo não é backup de módulo (backup-ade-modulo).' };
    }
    const { chaves, chavesIdb } = await restaurarEntradasBackupParcial(
      parsed.dados ?? {},
      parsed.indexedDB ?? {}
    );
    return {
      ok: true,
      modulo: parsed._modulo,
      label: parsed._label,
      chaves,
      chavesIdb,
    };
  } catch {
    return { ok: false, chaves: 0, chavesIdb: 0, erro: 'JSON inválido.' };
  }
}

/** Nome de arquivo rotativo seguro para Windows. */
export function nomeArquivoRotativo(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}.json`;
}
